import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import BlockIcon from '@material-ui/icons/Block';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import CloseIcon from '@material-ui/icons/Close';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import FilterListIcon from '@material-ui/icons/FilterList';
import LaunchIcon from '@material-ui/icons/Launch';
import TuneIcon from '@material-ui/icons/Tune';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import ringImage from '../../../assets/images/merchandising/jewelry-ring-white.svg';
import classicRingImage from '../../../assets/images/merchandising/jewelry-ring-classic.svg';
import earringsImage from '../../../assets/images/merchandising/jewelry-earrings-white.svg';
import pendantImage from '../../../assets/images/merchandising/jewelry-pendant-white.svg';
import goldRingImage from '../../../assets/images/merchandising/jewelry-ring-gold.svg';
import goldEarringsImage from '../../../assets/images/merchandising/jewelry-earrings-gold.svg';
import {
  InlineNotice,
  PageHeader,
  SegmentedControl,
  WorkbenchPagination,
} from './MerchandisingWorkbenchUi';
import { ProductCard } from './SearchRuleCards';
import useMerchandisingWorkbenchCopy from './useMerchandisingWorkbenchCopy';

const responseOptions = (t) => [
  { VALUE: 'search', LABEL: t('merchandising.advancedPrototype.responseSearch') },
  { VALUE: 'selection', LABEL: t('merchandising.advancedPrototype.responseSelection') },
  { VALUE: 'empty', LABEL: t('merchandising.advancedPrototype.responseEmpty') },
];

const previewOptions = (t) => [
  { VALUE: 'draft', LABEL: t('merchandising.advancedPrototype.previewDraft') },
  { VALUE: 'original', LABEL: t('merchandising.advancedPrototype.previewOriginal') },
  { VALUE: 'changes', LABEL: t('merchandising.advancedPrototype.previewChanges') },
];

const matchOptions = (t) => [
  { VALUE: 'exact', LABEL: t('merchandising.advancedPrototype.matchExact') },
  { VALUE: 'forms', LABEL: t('merchandising.advancedPrototype.matchForms') },
  { VALUE: 'concept', LABEL: t('merchandising.advancedPrototype.matchConcept') },
];

const initialSelectionGroups = (t) => [
  {
    id: 'rings-white-gold',
    category: t('merchandising.advancedPrototype.product.categoryRings'),
    material: t('merchandising.advancedPrototype.product.materialWhiteGold'),
    maxPrice: '',
  },
  {
    id: 'earrings-children',
    category: t('merchandising.advancedPrototype.product.categoryEarrings'),
    material: t('merchandising.advancedPrototype.product.audienceChildren'),
    maxPrice: '20000',
  },
];

const fixtureProducts = (t) => ({
  search: [
    {
      ID: 'gold-ring',
      PRODUCT_NAME: t('merchandising.advancedPrototype.product.goldRing'),
      IMAGE: goldRingImage,
      SKU: 'AU-R-1042',
      PRICE: '$1,190',
      MATERIAL: t('merchandising.advancedPrototype.product.materialYellowGold'),
      CATEGORY: t('merchandising.advancedPrototype.product.categoryRings'),
    },
    {
      ID: 'gold-earrings',
      PRODUCT_NAME: t('merchandising.advancedPrototype.product.goldEarrings'),
      IMAGE: goldEarringsImage,
      SKU: 'AU-E-2217',
      PRICE: '$860',
      MATERIAL: t('merchandising.advancedPrototype.product.materialYellowGold'),
      CATEGORY: t('merchandising.advancedPrototype.product.categoryEarrings'),
    },
  ],
  selection: [
    {
      ID: 'white-ring-diamonds',
      PRODUCT_NAME: t('merchandising.advancedPrototype.product.ringWhiteGold'),
      IMAGE: ringImage,
      SKU: 'WG-R-4102',
      PRICE: '$1,420',
      MATERIAL: t('merchandising.advancedPrototype.product.materialWhiteGold'),
      CATEGORY: t('merchandising.advancedPrototype.product.categoryRings'),
    },
    {
      ID: 'white-ring-classic',
      PRODUCT_NAME: t('merchandising.advancedPrototype.product.ringClassic'),
      IMAGE: classicRingImage,
      SKU: 'WG-R-3064',
      PRICE: '$980',
      MATERIAL: t('merchandising.advancedPrototype.product.materialWhiteGold'),
      CATEGORY: t('merchandising.advancedPrototype.product.categoryRings'),
    },
    {
      ID: 'diamond-earrings',
      PRODUCT_NAME: t('merchandising.advancedPrototype.product.earrings'),
      IMAGE: earringsImage,
      SKU: 'WG-E-1188',
      PRICE: '$720',
      MATERIAL: t('merchandising.advancedPrototype.product.materialDiamonds'),
      CATEGORY: t('merchandising.advancedPrototype.product.categoryEarrings'),
    },
    {
      ID: 'heart-pendant',
      PRODUCT_NAME: t('merchandising.advancedPrototype.product.pendant'),
      IMAGE: pendantImage,
      SKU: 'WG-P-7701',
      PRICE: '$640',
      MATERIAL: t('merchandising.advancedPrototype.product.materialWhiteGold'),
      CATEGORY: t('merchandising.advancedPrototype.product.categoryPendants'),
    },
  ],
});

const reorder = (ids, sourceId, targetId) => {
  const sourceIndex = ids.indexOf(sourceId);
  const targetIndex = ids.indexOf(targetId);
  if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return ids;
  const next = [...ids];
  next.splice(sourceIndex, 1);
  next.splice(targetIndex, 0, sourceId);
  return next;
};

const MatchSettingsDialog = ({ open, onClose, mode, onModeChange, t }) => {
  const aliases = [
    t('merchandising.advancedPrototype.aliasSilver'),
    t('merchandising.advancedPrototype.aliasSilverAdjective'),
    t('merchandising.advancedPrototype.aliasSterling'),
  ];
  const exclusions = [
    t('merchandising.advancedPrototype.exclusionWithoutSilver'),
    t('merchandising.advancedPrototype.exclusionNotSilver'),
  ];
  const [testQuery, setTestQuery] = useState('');
  const normalized = testQuery.trim().toLowerCase();
  const excluded = exclusions.some((entry) => normalized.includes(entry.toLowerCase()));
  const matched = mode === 'exact'
    ? normalized === t('merchandising.advancedPrototype.queryValue').toLowerCase()
    : aliases.some((entry) => normalized.includes(entry.toLowerCase()));
  const result = !normalized
    ? t('merchandising.advancedPrototype.matchPrompt')
    : (excluded
      ? t('merchandising.advancedPrototype.matchExcluded')
      : (matched
        ? t('merchandising.advancedPrototype.matchPositive')
        : t('merchandising.advancedPrototype.matchNegative')));

  return (
    <Dialog
      className="mp-dialog mp-advanced-match-dialog"
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="advanced-match-dialog-title"
    >
      <DialogTitle className="mp-dialog-title-row" id="advanced-match-dialog-title">
        <span>{t('merchandising.advancedPrototype.matchDialogTitle')}</span>
        <IconButton aria-label={t('merchandising.advancedPrototype.close')} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <p className="mp-advanced-dialog-intro">
          {t('merchandising.advancedPrototype.matchDialogBody')}
        </p>
        <SegmentedControl
          value={mode}
          options={matchOptions(t)}
          onChange={onModeChange}
          label={t('merchandising.advancedPrototype.matchModeLabel')}
        />
        {mode === 'concept' && (
          <div className="mp-advanced-concept-grid">
            <section>
              <strong>{t('merchandising.advancedPrototype.aliases')}</strong>
              <div>{aliases.map((alias) => <Chip key={alias} size="small" label={alias} />)}</div>
            </section>
            <section>
              <strong>{t('merchandising.advancedPrototype.exclusions')}</strong>
              <div>{exclusions.map((phrase) => <Chip key={phrase} size="small" label={phrase} />)}</div>
            </section>
          </div>
        )}
        <label className="mp-advanced-field mp-advanced-match-test">
          <span>{t('merchandising.advancedPrototype.testQuery')}</span>
          <input
            value={testQuery}
            placeholder={t('merchandising.advancedPrototype.testQueryPlaceholder')}
            onChange={(event) => setTestQuery(event.target.value)}
          />
        </label>
        <div
          className={`mp-advanced-match-result ${excluded ? 'is-excluded' : ''} ${matched && !excluded ? 'is-matched' : ''}`}
          role="status"
        >
          {result}
        </div>
      </DialogContent>
      <DialogActions className="mp-dialog-actions">
        <Button variant="contained" color="primary" onClick={onClose}>
          {t('merchandising.advancedPrototype.done')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SelectionDialog = ({ open, groups, onSave, onClose, t }) => {
  const [draft, setDraft] = useState(() => groups.map((group) => ({ ...group })));
  const [confirmClose, setConfirmClose] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(groups);

  React.useEffect(() => {
    if (open) setDraft(groups.map((group) => ({ ...group })));
  }, [groups, open]);

  const updateGroup = (id, key, value) => {
    setDraft((current) => current.map((group) => (
      group.id === id ? { ...group, [key]: value } : group
    )));
  };
  const requestClose = () => {
    if (dirty) setConfirmClose(true);
    else onClose();
  };
  const addGroup = () => {
    const number = draft.length + 1;
    setDraft((current) => [...current, {
      id: `prototype-group-${number}`,
      category: '',
      material: '',
      maxPrice: '',
    }]);
  };

  return (
    <>
      <Dialog
        className="mp-dialog mp-advanced-selection-dialog"
        open={open}
        onClose={requestClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="advanced-selection-dialog-title"
      >
        <DialogTitle className="mp-dialog-title-row" id="advanced-selection-dialog-title">
          <span>{t('merchandising.advancedPrototype.selectionDialogTitle')}</span>
          <IconButton aria-label={t('merchandising.advancedPrototype.close')} onClick={requestClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <p className="mp-advanced-dialog-intro">
            {t('merchandising.advancedPrototype.selectionDialogBody')}
          </p>
          <div className="mp-advanced-selection-groups">
            {draft.map((group, index) => (
              <React.Fragment key={group.id}>
                {index > 0 && <div className="mp-advanced-or">{t('merchandising.advancedPrototype.or')}</div>}
                <section className="mp-advanced-selection-group">
                  <header>
                    <div>
                      <span>{index + 1}</span>
                      <strong>{t('merchandising.advancedPrototype.group', { number: index + 1 })}</strong>
                    </div>
                    {draft.length > 1 && (
                      <IconButton
                        aria-label={t('merchandising.advancedPrototype.groupRemove', { number: index + 1 })}
                        onClick={() => setDraft((current) => current.filter((item) => item.id !== group.id))}
                      >
                        <CloseIcon />
                      </IconButton>
                    )}
                  </header>
                  <div className="mp-advanced-selection-fields">
                    <label className="mp-advanced-field">
                      <span>{t('merchandising.advancedPrototype.category')}</span>
                      <input
                        aria-label={t('merchandising.advancedPrototype.groupCategory', { number: index + 1 })}
                        value={group.category}
                        onChange={(event) => updateGroup(group.id, 'category', event.target.value)}
                      />
                    </label>
                    <label className="mp-advanced-field">
                      <span>{t('merchandising.advancedPrototype.material')}</span>
                      <input
                        aria-label={t('merchandising.advancedPrototype.groupMaterial', { number: index + 1 })}
                        value={group.material}
                        onChange={(event) => updateGroup(group.id, 'material', event.target.value)}
                      />
                    </label>
                    <label className="mp-advanced-field">
                      <span>{t('merchandising.advancedPrototype.maxPrice')}</span>
                      <input
                        aria-label={t('merchandising.advancedPrototype.groupPrice', { number: index + 1 })}
                        type="number"
                        value={group.maxPrice}
                        onChange={(event) => updateGroup(group.id, 'maxPrice', event.target.value)}
                      />
                    </label>
                  </div>
                  <div className="mp-advanced-selection-tokens">
                    {group.category && <Chip size="small" label={group.category} />}
                    {group.material && <Chip size="small" label={group.material} />}
                  </div>
                </section>
              </React.Fragment>
            ))}
          </div>
          <Button className="mp-advanced-add-group" startIcon={<AddIcon />} onClick={addGroup}>
            {t('merchandising.advancedPrototype.addGroup')}
          </Button>
        </DialogContent>
        <DialogActions className="mp-dialog-actions">
          <Button onClick={requestClose}>{t('merchandising.advancedPrototype.cancel')}</Button>
          <Button variant="contained" color="primary" onClick={() => onSave(draft)}>
            {t('merchandising.advancedPrototype.saveSelection')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        className="mp-dialog"
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="advanced-selection-discard-title"
      >
        <DialogTitle id="advanced-selection-discard-title">
          {t('merchandising.advancedPrototype.discardTitle')}
        </DialogTitle>
        <DialogContent><p>{t('merchandising.advancedPrototype.discardBody')}</p></DialogContent>
        <DialogActions className="mp-dialog-actions">
          <Button onClick={() => setConfirmClose(false)}>
            {t('merchandising.advancedPrototype.keepEditing')}
          </Button>
          <Button className="mp-danger-button" onClick={() => { setConfirmClose(false); onClose(); }}>
            {t('merchandising.advancedPrototype.discard')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const AdvancedSearchRulePrototype = ({ basePath }) => {
  const t = useLocalizedMessage();
  const C = useMerchandisingWorkbenchCopy();
  const history = useHistory();
  const products = useMemo(() => fixtureProducts(t), [t]);
  const [query, setQuery] = useState(t('merchandising.advancedPrototype.queryValue'));
  const [responseMode, setResponseMode] = useState('search');
  const [previewMode, setPreviewMode] = useState('draft');
  const [matchMode, setMatchMode] = useState('concept');
  const [matchOpen, setMatchOpen] = useState(false);
  const [selectionOpen, setSelectionOpen] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [selectionGroups, setSelectionGroups] = useState(() => initialSelectionGroups(t));
  const [rewrite, setRewrite] = useState('');
  const [emptyMessage, setEmptyMessage] = useState(t('merchandising.advancedPrototype.emptyDefaultMessage'));
  const [linkEnabled, setLinkEnabled] = useState(false);
  const [linkLabel, setLinkLabel] = useState(t('merchandising.advancedPrototype.emptyLinkDefaultLabel'));
  const [linkUrl, setLinkUrl] = useState(t('merchandising.advancedPrototype.emptyLinkDefaultUrl'));
  const [notice, setNotice] = useState(t('merchandising.advancedPrototype.localOnly'));
  const [changes, setChanges] = useState({});
  const allProductIds = [...products.search, ...products.selection].map((product) => product.ID);
  const [productOrder, setProductOrder] = useState(allProductIds);
  const [draggedProduct, setDraggedProduct] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const validLink = linkUrl.startsWith('/') && !linkUrl.startsWith('//');

  const baseProducts = previewMode === 'original'
    ? products.search
    : (responseMode === 'selection' ? products.selection : products.search);
  const orderedProducts = productOrder
    .map((id) => baseProducts.find((product) => product.ID === id))
    .filter(Boolean);
  const previewProducts = previewMode === 'changes'
    ? orderedProducts.filter((product) => changes[product.ID])
    : orderedProducts;

  const updateChange = (id, change) => setChanges((current) => {
    if (!change) {
      const next = { ...current };
      delete next[id];
      return next;
    }
    return { ...current, [id]: change };
  });
  const moveProduct = (id, direction) => setProductOrder((current) => {
    const index = current.indexOf(id);
    const target = Math.max(0, Math.min(current.length - 1, index + direction));
    if (index < 0 || target === index) return current;
    return reorder(current, id, current[target]);
  });
  const dropProduct = (event, targetId) => {
    event.preventDefault();
    if (!draggedProduct) return;
    setProductOrder((current) => reorder(current, draggedProduct, targetId));
    const targetPosition = Math.max(1, orderedProducts.findIndex((product) => product.ID === targetId) + 1);
    updateChange(draggedProduct, { TYPE: 'pinned', POSITION: targetPosition });
    setDraggedProduct(null);
    setDropTarget(null);
  };

  const productCardCopy = {
    ...C,
    TIMES: C.TIMES,
  };

  return (
    <div className="mp-advanced-prototype">
      <PageHeader
        eyebrow={t('merchandising.advancedPrototype.eyebrow')}
        heading={t('merchandising.advancedPrototype.title')}
        body={t('merchandising.advancedPrototype.description')}
        primaryLabel={t('merchandising.advancedPrototype.previewAction')}
        primaryIcon={<TuneIcon />}
        onPrimary={() => setNotice(t('merchandising.advancedPrototype.localUpdated'))}
        secondary={(
          <Button startIcon={<ChevronLeftIcon />} onClick={() => history.push(`${basePath}/query`)}>
            {t('merchandising.advancedPrototype.back')}
          </Button>
        )}
      />

      <InlineNotice tone="neutral">
        {notice}
      </InlineNotice>

      <div className="mp-query-bar mp-advanced-query-bar">
        <label>
          <span>{t('merchandising.advancedPrototype.query')}</span>
          <input
            aria-label={t('merchandising.advancedPrototype.query')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <Button
          variant="outlined"
          startIcon={<TuneIcon />}
          onClick={() => setMatchOpen(true)}
        >
          {matchMode === 'concept'
            ? t('merchandising.advancedPrototype.conceptMatching')
            : matchOptions(t).find((option) => option.VALUE === matchMode).LABEL}
        </Button>
        <Button
          className="mp-mobile-inspector-trigger"
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setInspectorOpen(true)}
        >
          {t('merchandising.advancedPrototype.ruleSettings')}
        </Button>
      </div>

      <div className="mp-workbench-layout mp-advanced-workbench-layout">
        <section className="mp-panel mp-preview-panel mp-advanced-preview" aria-label={t('merchandising.advancedPrototype.previewLabel')}>
          <header className="mp-preview-header mp-advanced-preview-header">
            <div>
              <span>{t('merchandising.advancedPrototype.previewFor', { query })}</span>
              <strong>
                {responseMode === 'selection'
                  ? t('merchandising.advancedPrototype.selectionBody')
                  : (previewMode === 'original'
                    ? t('merchandising.advancedPrototype.originalContext')
                    : t('merchandising.advancedPrototype.productsFound', { count: previewProducts.length }))}
              </strong>
            </div>
            <SegmentedControl
              value={previewMode}
              options={previewOptions(t)}
              onChange={setPreviewMode}
              label={t('merchandising.advancedPrototype.previewMode')}
            />
          </header>

          {responseMode === 'empty' && previewMode === 'draft' ? (
            <div className="mp-advanced-empty-preview">
              <span><BlockIcon /></span>
              <small>{t('merchandising.advancedPrototype.emptyBadge')}</small>
              <h2>{t('merchandising.advancedPrototype.emptyTitle')}</h2>
              <p>{emptyMessage}</p>
              {linkEnabled && validLink && (
                <Button
                  component="a"
                  href={linkUrl}
                  variant="outlined"
                  endIcon={<LaunchIcon />}
                >
                  {linkLabel}
                </Button>
              )}
            </div>
          ) : (
            <>
              {responseMode === 'selection' && previewMode === 'draft' && (
                <div className="mp-advanced-preview-context">
                  <FilterListIcon />
                  <span>{t('merchandising.advancedPrototype.selectionSummary')}</span>
                </div>
              )}
              <div className="mp-product-grid mp-advanced-product-grid">
                {previewProducts.map((product, index) => {
                  const change = changes[product.ID];
                  return (
                    <ProductCard
                      key={product.ID}
                      product={product}
                      change={change}
                      displayPosition={index + 1}
                      dragging={draggedProduct === product.ID}
                      dropTarget={dropTarget === product.ID}
                      groupExcluded={false}
                      groupFactor={1}
                      copy={productCardCopy}
                      onHide={() => updateChange(product.ID, change?.TYPE === 'hidden' ? null : { TYPE: 'hidden' })}
                      onPin={() => updateChange(product.ID, { TYPE: 'pinned', POSITION: index + 1 })}
                      onUnpin={() => updateChange(product.ID, null)}
                      onDragStart={(event, id) => {
                        setDraggedProduct(id);
                        if (event.dataTransfer) event.dataTransfer.setData('text/plain', id);
                      }}
                      onDragEnter={setDropTarget}
                      onDragEnd={() => { setDraggedProduct(null); setDropTarget(null); }}
                      onDropAt={dropProduct}
                      onMoveEarlier={() => moveProduct(product.ID, -1)}
                      onMoveLater={() => moveProduct(product.ID, 1)}
                      interactive={previewMode === 'draft'}
                    />
                  );
                })}
              </div>
              {previewProducts.length > 0 && (
                <div className="mp-preview-pagination">
                  <span>{t('merchandising.advancedPrototype.productsFound', { count: previewProducts.length })}</span>
                  <WorkbenchPagination
                    page={1}
                    pageCount={3}
                    onChange={() => {}}
                    firstLabel={C.FIRST_PAGE}
                    previousLabel={C.PREVIOUS_PAGE}
                    nextLabel={C.NEXT_PAGE}
                    lastLabel={C.LAST_PAGE}
                  />
                </div>
              )}
            </>
          )}
        </section>

        <aside className={`mp-inspector mp-advanced-inspector ${inspectorOpen ? 'is-open' : ''}`} aria-label={t('merchandising.advancedPrototype.ruleSettings')}>
          <div className="mp-inspector-header">
            <div><span>{t('merchandising.advancedPrototype.ruleSettings')}</span></div>
            <IconButton
              className="mp-inspector-close"
              aria-label={t('merchandising.advancedPrototype.close')}
              onClick={() => setInspectorOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </div>
          <div className="mp-inspector-body">
            <section className="mp-setting-card mp-advanced-response-card">
              <div className="mp-inspector-section-heading">
                <div>
                  <strong>{t('merchandising.advancedPrototype.responseTitle')}</strong>
                  <span>{t('merchandising.advancedPrototype.responseHelp')}</span>
                </div>
              </div>
              <SegmentedControl
                value={responseMode}
                options={responseOptions(t)}
                onChange={(value) => { setResponseMode(value); setPreviewMode('draft'); }}
                label={t('merchandising.advancedPrototype.responseLabel')}
              />
            </section>

            {responseMode === 'search' && (
              <section className="mp-setting-card mp-advanced-mode-card">
                <strong>{t('merchandising.advancedPrototype.searchTitle')}</strong>
                <p>{t('merchandising.advancedPrototype.searchBody')}</p>
                <label className="mp-advanced-field">
                  <span>{t('merchandising.advancedPrototype.rewriteLabel')}</span>
                  <input
                    value={rewrite}
                    placeholder={t('merchandising.advancedPrototype.rewritePlaceholder')}
                    onChange={(event) => setRewrite(event.target.value)}
                  />
                </label>
              </section>
            )}

            {responseMode === 'selection' && (
              <section className="mp-setting-card mp-advanced-mode-card">
                <strong>{t('merchandising.advancedPrototype.selectionTitle')}</strong>
                <p>{t('merchandising.advancedPrototype.selectionSummary')}</p>
                <div className="mp-advanced-selection-summary">
                  <span>{t('merchandising.advancedPrototype.selectionGroups', { count: selectionGroups.length })}</span>
                  <div>
                    {selectionGroups.map((group) => (
                      <Chip key={group.id} size="small" label={[group.category, group.material].filter(Boolean).join(' · ')} />
                    ))}
                  </div>
                </div>
                <Button
                  variant="outlined"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => setSelectionOpen(true)}
                >
                  {t('merchandising.advancedPrototype.editSelection')}
                </Button>
              </section>
            )}

            {responseMode === 'empty' && (
              <section className="mp-setting-card mp-advanced-mode-card">
                <label className="mp-advanced-field">
                  <span>{t('merchandising.advancedPrototype.emptyMessage')}</span>
                  <textarea value={emptyMessage} onChange={(event) => setEmptyMessage(event.target.value)} />
                </label>
                <FormControlLabel
                  control={(
                    <Checkbox
                      color="primary"
                      checked={linkEnabled}
                      onChange={(event) => setLinkEnabled(event.target.checked)}
                    />
                  )}
                  label={t('merchandising.advancedPrototype.emptyLinkToggle')}
                />
                {linkEnabled && (
                  <div className="mp-advanced-link-fields">
                    <label className="mp-advanced-field">
                      <span>{t('merchandising.advancedPrototype.emptyLinkLabel')}</span>
                      <input value={linkLabel} onChange={(event) => setLinkLabel(event.target.value)} />
                    </label>
                    <label className="mp-advanced-field">
                      <span>{t('merchandising.advancedPrototype.emptyLinkUrl')}</span>
                      <input value={linkUrl} onChange={(event) => setLinkUrl(event.target.value)} />
                    </label>
                  </div>
                )}
              </section>
            )}

            <section className="mp-setting-card mp-advanced-schedule-note">
              {t('merchandising.advancedPrototype.prototypeSchedule')}
            </section>
          </div>
        </aside>
      </div>

      <MatchSettingsDialog
        open={matchOpen}
        onClose={() => setMatchOpen(false)}
        mode={matchMode}
        onModeChange={setMatchMode}
        t={t}
      />
      <SelectionDialog
        open={selectionOpen}
        groups={selectionGroups}
        onClose={() => setSelectionOpen(false)}
        onSave={(next) => { setSelectionGroups(next); setSelectionOpen(false); }}
        t={t}
      />
    </div>
  );
};

AdvancedSearchRulePrototype.propTypes = { basePath: PropTypes.string };
AdvancedSearchRulePrototype.defaultProps = { basePath: '/search/merchandising/rules' };

export default AdvancedSearchRulePrototype;
